import { Box, Grid, Icon } from '@mui/material';
import React from 'react';
import { CheckCircle, PriorityHigh } from '@mui/icons-material';
import OrderUtils from '../../order_page/OrderUtils';
import { AccountData, OrderData, OrderPageData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { CalendarBoxTypography } from './CalendarBoxContext';
import { getContrastColor } from '../../common/ui/getContrastColor';
import { CalendarView } from '../calendarWrapper/CalendarWrapper';
import { useCalendar } from '../CalendarContext/CalendarContext';

type OrderTopBarProps = {
  orderPage: OrderPageData;
  order: OrderData;
};
// Currently used only for rendering stations mark for speculo but a little bit generic. If we are going
// to render non numbers or more then one item, some ux should be improved
// Now refactored as a top toolbar that arranges relevant icons for the order from left to right
export const OrderTopBar = ({ orderPage, order }: OrderTopBarProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { calendarView } = useCalendar();

  // Function to determine if a color is green-ish
  const isGreenishColor = (color: string): boolean => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Consider it green-ish if green component is dominant
    return g > r && g > b && g > 100;
  };

  // Get contrasting color for check icon
  const getCheckIconColor = (): string => {
    const bgColor = orderPage?.color || '#6577b3';
    return isGreenishColor(bgColor) ? '#fff' : '#0DF205';
  };
  const customDetailsNamesToRender = OrderUtils.getShowableCustomDetails(orderPage);
  const customDetailsToRender: any[] = [];
  if (customDetailsNamesToRender.length > 0) {
    customDetailsNamesToRender.forEach((x) => {
      // for now we just render stations which are a number and now label.
      if (order.custom_details) {
        customDetailsToRender.push(order.custom_details[x.name]);
      }
    });
  }

  const resourcesRequired = account?.shared_resources?.automatic_dividable_resources
    ?.map((resource) => {
      const allocation = orderPage.shared_resources_allocations?.automatic_dividable_resources?.find(
        (a) => a.resource_id === resource.id,
      );
      if (!allocation) return null;

      const required = Math.ceil(order.persons / allocation.unit_capacity);
      return required > 1 ? { id: resource.id, required } : null;
    })
    .filter(Boolean);
  return (
    <Grid
      container
      direction="row"
      gap={0.5}
      px={0.5}
      alignItems="center"
      justifyContent="space-between"
      sx={{ zIndex: 100 }}>
      <Grid container direction="row" gap={0.5} alignItems="center" sx={{ flex: 1 }}>
        {/* Persons count - first item */}
        <Grid item>
          <CalendarBoxTypography sx={{ fontSize: '16px', fontWeight: 700 }}>{order.persons}</CalendarBoxTypography>
        </Grid>

        {/* Resources required - third items */}
        {resourcesRequired?.map((resource) => (
          <Grid
            key={`resource-${resource?.id}`}
            item
            sx={{
              width: '14px',
              height: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '0.5px solid #ff9800',
              borderRadius: '50%',
              fontSize: '8px',
              fontWeight: 'bold',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
            }}>
            {resource?.required}
          </Grid>
        ))}

        {/* Boolean custom details - show icons for true values in day view */}
        {calendarView === CalendarView.DAY &&
          orderPage.custom_details?.map((custom_detail: any, index: number) => {
            if (custom_detail.type === 'bool' && order.custom_details && order.custom_details[custom_detail.name]) {
              return (
                <Grid key={`bool-custom-${index}`} item>
                  {custom_detail.icon.endsWith('.svg') ? (
                    <img
                      src={custom_detail.icon}
                      alt=""
                      style={{
                        width: '16px',
                        height: '16px',
                        filter: `brightness(0) saturate(100%) invert(${
                          getContrastColor(orderPage?.color) === '#ffffff' ? '100%' : '0%'
                        })`,
                      }}
                    />
                  ) : (
                    <Icon
                      sx={{
                        width: '16px',
                        height: '16px',
                        fontSize: '16px',
                        fill: getContrastColor(orderPage?.color),
                      }}>
                      {custom_detail.icon}
                    </Icon>
                  )}
                </Grid>
              );
            }
            return null;
          })}

        {/* Custom details - remaining items */}
        {customDetailsToRender.length > 0 &&
          customDetailsToRender.map((x, index) => {
            if (x < 2) {
              return null;
            }
            return (
              <Grid
                key={`customdetail-${x}-${index}`}
                item
                sx={{
                  width: '14px',
                  height: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${x === 2 ? '#00ff63' : '#7ceee1'}`,
                  borderRadius: '50%',
                  fontSize: '8px',
                  fontWeight: 'bold',
                  backgroundColor: x === 2 ? 'rgba(0, 255, 99, 0.1)' : 'rgba(124, 238, 225, 0.1)',
                }}>
                {x}
              </Grid>
            );
          })}
      </Grid>

      {/* Right side icons */}
      <Grid container direction="row" gap={0.5} alignItems="center" sx={{ flex: 0 }}>
        {/* Comment icon */}
        {(order.comment ||
          orderPage.custom_personal_details?.some(
            (detail) =>
              detail.is_comment_field &&
              order.custom_personal_details &&
              order.custom_personal_details[detail.id] &&
              order.custom_personal_details[detail.id],
          )) && (
          <Grid item>
            <Box
              sx={{
                backgroundColor: '#ffebee',
                borderRadius: '50%',
                padding: '1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <PriorityHigh sx={{ width: '16px', height: '16px', fill: 'red', fontWeight: 700 }} />
            </Box>
          </Grid>
        )}

        {/* Check-in icon */}
        {order.checkin?.created_at && (
          <Grid item>
            <CheckCircle sx={{ width: '12px', height: '12px', fill: getCheckIconColor() }} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
